import { useState, useRef, useEffect, useCallback, useMemo } from 'react';

export interface MixerSource {
  type: 'music' | 'voice';
  url?: string;
  blob?: Blob;
  text?: string;
}

export interface MixerState {
  isPlaying: boolean;
  isPaused: boolean;
  /**
   * True when playback reached its natural end (not manual stop/pause/seek):
   * either the voice track finished, or — for music-only playback — the music
   * track completed its single pass (music only loops under a voice track).
   */
  isFinished: boolean;
  currentTime: number;
  duration: number;
  musicVolume: number;
  voiceVolume: number;
  isLoading: boolean;
  error: string | null;
}

export interface MixerControls {
  loadAndPlay: (musicSource?: MixerSource, voiceSource?: MixerSource) => Promise<void>;
  play: () => void;
  pause: () => void;
  stop: () => void;
  setMusicVolume: (volume: number) => void;
  setVoiceVolume: (volume: number) => void;
  seek: (time: number) => void;
}

export function useAudioMixer() {
  const audioContextRef = useRef<AudioContext | null>(null);
  const musicSourceRef = useRef<AudioBufferSourceNode | null>(null);
  const voiceSourceRef = useRef<AudioBufferSourceNode | null>(null);
  const musicGainRef = useRef<GainNode | null>(null);
  const voiceGainRef = useRef<GainNode | null>(null);
  const startTimeRef = useRef<number>(0);
  const pauseTimeRef = useRef<number>(0);
  const musicBufferRef = useRef<AudioBuffer | null>(null);
  const voiceBufferRef = useRef<AudioBuffer | null>(null);
  const animationFrameRef = useRef<number | null>(null);
  const manualStopRef = useRef<boolean>(false);

  const [state, setState] = useState<MixerState>({
    isPlaying: false,
    isPaused: false,
    isFinished: false,
    currentTime: 0,
    duration: 0,
    musicVolume: 30,
    voiceVolume: 70,
    isLoading: false,
    error: null,
  });

  useEffect(() => {
    const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext;
    if (!AudioContextClass) {
      setState(prev => ({ ...prev, error: 'Web Audio API not supported' }));
      return;
    }

    audioContextRef.current = new AudioContextClass();

    return () => {
      if (audioContextRef.current) {
        audioContextRef.current.close();
      }
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, []);

  const updateTime = useCallback(() => {
    if (audioContextRef.current && state.isPlaying && !state.isPaused) {
      const currentTime = audioContextRef.current.currentTime - startTimeRef.current;
      setState(prev => ({ ...prev, currentTime }));
      animationFrameRef.current = requestAnimationFrame(updateTime);
    }
  }, [state.isPlaying, state.isPaused]);

  const fetchAndDecodeAudio = useCallback(async (url: string): Promise<AudioBuffer> => {
    if (!audioContextRef.current) {
      throw new Error('Audio context not initialized');
    }

    const response = await fetch(url);
    const arrayBuffer = await response.arrayBuffer();
    return await audioContextRef.current.decodeAudioData(arrayBuffer);
  }, []);

  const blobToAudioBuffer = useCallback(async (blob: Blob): Promise<AudioBuffer> => {
    if (!audioContextRef.current) {
      throw new Error('Audio context not initialized');
    }

    const arrayBuffer = await blob.arrayBuffer();
    return await audioContextRef.current.decodeAudioData(arrayBuffer);
  }, []);

  const loadAndPlay = useCallback(async (musicSource?: MixerSource, voiceSource?: MixerSource) => {
    if (!audioContextRef.current) {
      setState(prev => ({ ...prev, error: 'Audio context not available' }));
      return;
    }

    setState(prev => ({ ...prev, isLoading: true, error: null, isFinished: false }));

    try {
      manualStopRef.current = true;
      if (musicSourceRef.current) {
        musicSourceRef.current.stop();
      }
      if (voiceSourceRef.current) {
        voiceSourceRef.current.stop();
      }

      let musicBuffer: AudioBuffer | null = null;
      let voiceBuffer: AudioBuffer | null = null;

      if (musicSource?.url) {
        musicBuffer = await fetchAndDecodeAudio(musicSource.url);
        musicBufferRef.current = musicBuffer;
      }

      if (voiceSource?.blob) {
        voiceBuffer = await blobToAudioBuffer(voiceSource.blob);
        voiceBufferRef.current = voiceBuffer;
      } else if (voiceSource?.text) {
        setState(prev => ({ ...prev, error: 'TTS integration needed for text voice source' }));
      }

      const duration = Math.max(
        musicBuffer?.duration || 0,
        voiceBuffer?.duration || 0
      );

      setState(prev => ({ 
        ...prev, 
        duration,
        isLoading: false,
      }));

      if (musicBuffer || voiceBuffer) {
        await playBuffers(musicBuffer, voiceBuffer);
      }
    } catch (error) {
      console.error('Error loading audio:', error);
      setState(prev => ({ 
        ...prev, 
        isLoading: false,
        error: error instanceof Error ? error.message : 'Failed to load audio',
      }));
    }
  }, [fetchAndDecodeAudio, blobToAudioBuffer]);

  const playBuffers = useCallback(async (musicBuffer: AudioBuffer | null, voiceBuffer: AudioBuffer | null) => {
    if (!audioContextRef.current) return;

    const context = audioContextRef.current;

    if (context.state === 'suspended') {
      await context.resume();
    }

    if (!musicGainRef.current) {
      musicGainRef.current = context.createGain();
      musicGainRef.current.connect(context.destination);
    }
    if (!voiceGainRef.current) {
      voiceGainRef.current = context.createGain();
      voiceGainRef.current.connect(context.destination);
    }

    musicGainRef.current.gain.value = state.musicVolume / 100;
    voiceGainRef.current.gain.value = state.voiceVolume / 100;

    if (musicBuffer) {
      const musicSource = context.createBufferSource();
      musicSource.buffer = musicBuffer;
      musicSource.connect(musicGainRef.current);
      // Loop the music only while a voice track is playing (music underlays the
      // voice and is stopped when the voice ends). For a music-only meditation,
      // play a single pass and report a natural finish so the session can end.
      musicSource.loop = !!voiceBuffer;
      musicSource.start(0, pauseTimeRef.current);
      musicSourceRef.current = musicSource;

      if (!voiceBuffer) {
        musicSource.onended = () => {
          if (manualStopRef.current) return;
          if (animationFrameRef.current) {
            cancelAnimationFrame(animationFrameRef.current);
          }
          setState(prev => ({ ...prev, isPlaying: false, isFinished: true, currentTime: 0 }));
          pauseTimeRef.current = 0;
          startTimeRef.current = 0;
        };
      }
    }

    if (voiceBuffer) {
      const voiceSource = context.createBufferSource();
      voiceSource.buffer = voiceBuffer;
      voiceSource.connect(voiceGainRef.current);
      voiceSource.start(0, pauseTimeRef.current);
      voiceSourceRef.current = voiceSource;

      voiceSource.onended = () => {
        if (manualStopRef.current) return;
        // Natural end of the voice track: stop the looping music too so no audio lingers
        if (musicSourceRef.current) {
          try {
            musicSourceRef.current.stop();
          } catch {
            // already stopped
          }
        }
        if (animationFrameRef.current) {
          cancelAnimationFrame(animationFrameRef.current);
        }
        setState(prev => ({ ...prev, isPlaying: false, isFinished: true, currentTime: 0 }));
        pauseTimeRef.current = 0;
        startTimeRef.current = 0;
      };
    }

    startTimeRef.current = context.currentTime - pauseTimeRef.current;
    manualStopRef.current = false;
    setState(prev => ({ ...prev, isPlaying: true, isPaused: false, isFinished: false }));
    updateTime();
  }, [state.musicVolume, state.voiceVolume, updateTime]);

  const play = useCallback(() => {
    if (musicBufferRef.current || voiceBufferRef.current) {
      playBuffers(musicBufferRef.current, voiceBufferRef.current);
    }
  }, [playBuffers]);

  const pause = useCallback(() => {
    manualStopRef.current = true;
    if (musicSourceRef.current) {
      musicSourceRef.current.stop();
    }
    if (voiceSourceRef.current) {
      voiceSourceRef.current.stop();
    }

    pauseTimeRef.current = state.currentTime;
    setState(prev => ({ ...prev, isPlaying: false, isPaused: true }));

    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
    }
  }, [state.currentTime]);

  const stop = useCallback(() => {
    manualStopRef.current = true;
    if (musicSourceRef.current) {
      musicSourceRef.current.stop();
    }
    if (voiceSourceRef.current) {
      voiceSourceRef.current.stop();
    }

    pauseTimeRef.current = 0;
    startTimeRef.current = 0;
    setState(prev => ({ 
      ...prev, 
      isPlaying: false, 
      isPaused: false,
      currentTime: 0,
    }));

    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
    }
  }, []);

  const setMusicVolume = useCallback((volume: number) => {
    const clampedVolume = Math.max(0, Math.min(100, volume));
    setState(prev => ({ ...prev, musicVolume: clampedVolume }));

    if (musicGainRef.current) {
      musicGainRef.current.gain.value = clampedVolume / 100;
    }
  }, []);

  const setVoiceVolume = useCallback((volume: number) => {
    const clampedVolume = Math.max(0, Math.min(100, volume));
    setState(prev => ({ ...prev, voiceVolume: clampedVolume }));

    if (voiceGainRef.current) {
      voiceGainRef.current.gain.value = clampedVolume / 100;
    }
  }, []);

  const seek = useCallback((time: number) => {
    const wasPlaying = state.isPlaying;

    manualStopRef.current = true;
    if (musicSourceRef.current) {
      musicSourceRef.current.stop();
    }
    if (voiceSourceRef.current) {
      voiceSourceRef.current.stop();
    }

    pauseTimeRef.current = time;
    setState(prev => ({ ...prev, currentTime: time, isPlaying: false }));

    if (wasPlaying && (musicBufferRef.current || voiceBufferRef.current)) {
      setTimeout(() => {
        playBuffers(musicBufferRef.current, voiceBufferRef.current);
      }, 100);
    }
  }, [state.isPlaying, playBuffers]);

  const controls: MixerControls = useMemo(() => ({
    loadAndPlay,
    play,
    pause,
    stop,
    setMusicVolume,
    setVoiceVolume,
    seek,
  }), [loadAndPlay, play, pause, stop, setMusicVolume, setVoiceVolume, seek]);

  return { state, controls };
}
